import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

const VerifyEmailPage = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Track if API call is already made
  const apiCallMade = useRef(false);

  // Extract token only once
  const token = React.useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("token");
  }, [location.search]);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token || apiCallMade.current) {
        return; // Prevent multiple API calls
      }

      apiCallMade.current = true; // Mark API call as made

      try {
        const response = await fetch(`/api/verify-email?token=${token}`, {
          method: "GET",
        });

        if (response.ok) {
          setMessage("Your email has been successfully verified! You will be redirected to the login page.");
          setIsSuccess(true);
          setLoading(false);

          // Clear the token from the URL to prevent further calls
          navigate(window.location.pathname, { replace: true });

          // Redirect after a delay
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        } else {
          const data = await response.json();
          setMessage(data.message || "Verification failed or token expired.");
          setIsSuccess(false);
          setLoading(false);
        }
      } catch (error) {
        setMessage("There was an error verifying your email. Please try again.");
        setIsSuccess(false);
        setLoading(false);
      }
    };

    verifyEmail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only runs when the token changes

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white shadow-xl rounded-lg p-8 w-full max-w-md">
        <h2 className="text-3xl font-semibold text-center text-gray-800 mb-6">Email Verification</h2>

        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full border-t-4 border-blue-600 w-12 h-12"></div>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              {isSuccess ? (
                <CheckCircleIcon className="text-green-500 h-16 w-16 mx-auto mb-4 animate__animated animate__fadeIn" />
              ) : (
                <XCircleIcon className="text-red-500 h-16 w-16 mx-auto mb-4 animate__animated animate__fadeIn" />
              )}
            </div>
            <p className="text-lg text-gray-700 text-center mb-6">{message}</p>

            {isSuccess && (
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  You will be redirected to the login page in a few seconds. If not,{" "}
                  <a href="/login" className="text-blue-500 font-semibold hover:underline">
                    click here to go to login
                  </a>
                  .
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
